// UsersService.js - Enhanced User Management Service
import axios from 'axios';

const API_BASE_URL = 'https://api.jamboricg.com';

// Create axios instance with default config
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('authToken');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export const UsersService = {
    // Authentication Services
    login: async (credentials) => {
        try {
            const response = await apiClient.post('/users/api/v1/login/', credentials);
            if (response.data.token) {
                localStorage.setItem('authToken', response.data.token);
            }
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    register: async (userData) => {
        try {
            const formData = new FormData();
            Object.keys(userData).forEach(key => {
                formData.append(key, userData[key]);
            });

            const response = await apiClient.post('/users/api/v1/register/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    logout: () => {
        localStorage.removeItem('authToken');
        window.location.href = '/login';
    },

    // Basic User Services
    getAllUsers: async () => {
        try {
            const response = await apiClient.get('/users/api/v1/allUsers/');
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    getCurrentUser: async () => {
        try {
            const response = await apiClient.get('/users/api/v1/singleUser/');
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Enhanced User Management Services
    getUsersWithPagination: async (page = 1, pageSize = 10, search = '', grade = '') => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                page_size: pageSize.toString(),
                ...(search && { search }),
                ...(grade && { grade })
            });

            const response = await apiClient.get(`/users/api/v1/users/management/?${params}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    getUserById: async (userId) => {
        try {
            const response = await apiClient.get(`/users/api/v1/users/management/${userId}/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    updateUser: async (userId, userData) => {
        try {
            const formData = new FormData();
            Object.keys(userData).forEach(key => {
                if (userData[key] !== null && userData[key] !== undefined) {
                    formData.append(key, userData[key]);
                }
            });

            const response = await apiClient.put(`/users/api/v1/users/management/${userId}/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    deleteUser: async (userId) => {
        try {
            const response = await apiClient.delete(`/users/api/v1/users/management/${userId}/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    toggleUserStatus: async (userId) => {
        try {
            const response = await apiClient.post(`/users/api/v1/users/${userId}/toggle-status/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Statistics and Analytics
    getUserStats: async () => {
        try {
            const response = await apiClient.get('/users/api/v1/users/stats/');
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Account Management Services
    getPreferredAccounts: async () => {
        try {
            const response = await apiClient.get('/users/api/v1/prefferedAccounts/');
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    getCompulsoryAccounts: async () => {
        try {
            const response = await apiClient.get('/users/api/v1/compulsoryAccounts/');
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    createPreferredAccount: async (accountData) => {
        try {
            const response = await apiClient.post('/users/api/v1/prefferedAccounts/', accountData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    createCompulsoryAccount: async (accountData) => {
        try {
            const response = await apiClient.post('/users/api/v1/compulsoryAccounts/', accountData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Bulk Operations
    bulkUserAction: async (userIds, action) => {
        try {
            const response = await apiClient.post('/users/api/v1/users/bulk-action/', {
                user_ids: userIds,
                action: action
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Search and Filter
    searchUsers: async (searchTerm, filters = {}) => {
        try {
            const params = new URLSearchParams({
                search: searchTerm,
                ...filters
            });

            const response = await apiClient.get(`/users/api/v1/users/search/?${params}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Export functionality
    exportUsers: async (format = 'csv', filters = {}) => {
        try {
            const params = new URLSearchParams({
                format,
                ...filters
            });

            const response = await apiClient.get(`/users/api/v1/users/export/?${params}`, {
                responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `users.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            return { success: true, message: 'Export completed successfully' };
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Utility functions
    isAuthenticated: () => {
        return !!localStorage.getItem('authToken');
    },

    getAuthToken: () => {
        return localStorage.getItem('authToken');
    },

    // Error handler helper
    handleError: (error) => {
        console.error('UsersService Error:', error);
        
        if (typeof error === 'string') {
            return { message: error };
        }
        
        if (error.message) {
            return { message: error.message };
        }
        
        if (error.detail) {
            return { message: error.detail };
        }
        // return error message here
        return { message: 'An unexpected error occurred' };
    },
     // NEW: Role Management Services
    getUserRoles: async (userId) => {
        try {
            const response = await apiClient.get(`/users/api/v1/users/${userId}/roles/info/`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    getAvailableRoles: async () => {
        try {
            const response = await apiClient.get('/users/api/v1/users/roles/available/');
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    updateUserRoles: async (userId, roleData) => {
        try {
            const response = await apiClient.put(`/users/api/v1/users/${userId}/roles/`, roleData);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Convenience methods for specific role changes
    makeUserAdmin: async (userId) => {
        try {
            const response = await apiClient.put(`/users/api/v1/users/${userId}/roles/`, {
                role_type: 'staff'
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    makeUserSuperAdmin: async (userId) => {
        try {
            const response = await apiClient.put(`/users/api/v1/users/${userId}/roles/`, {
                role_type: 'superuser'
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    removeUserAdminRights: async (userId) => {
        try {
            const response = await apiClient.put(`/users/api/v1/users/${userId}/roles/`, {
                role_type: 'regular'
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

    // Batch role updates
    updateMultipleUserRoles: async (roleUpdates) => {
        try {
            const promises = roleUpdates.map(update => 
                apiClient.put(`/users/api/v1/users/${update.userId}/roles/`, update.roleData)
            );
            const responses = await Promise.all(promises);
            return responses.map(response => response.data);
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },

     // ========== NEW: REWARD POINTS CONFIGURATION SERVICES ==========

    // Get reward points configuration
    getRewardPointsConfig: async () => {
        try {
            console.log('Fetching reward points configuration...');
            const response = await apiClient.get('/users/api/v1/admin/reward-points-config/');
            console.log('Configuration loaded successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching reward points config:', error);
            throw error.response?.data || error.message;
        }
    },

    // Update reward points configuration
    updateRewardPointsConfig: async (configData) => {
        try {
            console.log('Updating reward points configuration:', configData);
            const response = await apiClient.put('/users/api/v1/admin/reward-points-config/', configData);
            console.log('Configuration updated successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error updating reward points config:', error);
            throw error.response?.data || error.message;
        }
    },

    // Reset all reward points
    resetAllRewardPoints: async (confirmationData = {}) => {
        try {
            console.log('Resetting all reward points with confirmation:', confirmationData);
            
            const requestData = {
                confirm: true,
                confirm_all_users: true,
                ...confirmationData
            };

            const response = await apiClient.post('/users/api/v1/admin/reset-reward-points/', requestData);
            console.log('Reward points reset successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error resetting reward points:', error);
            throw error.response?.data || error.message;
        }
    },

    // Reset specific users' reward points
    resetUsersRewardPoints: async (userIds, confirmationData = {}) => {
        try {
            console.log('Resetting reward points for users:', userIds);
            
            const requestData = {
                user_ids: userIds,
                confirm: true,
                ...confirmationData
            };

            const response = await apiClient.post('/users/api/v1/admin/reset-reward-points/', requestData);
            console.log('Users reward points reset successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error resetting users reward points:', error);
            throw error.response?.data || error.message;
        }
    },

    // Reset reward points with filters
    resetFilteredUsersRewardPoints: async (filters, confirmationData = {}) => {
        try {
            console.log('Resetting reward points with filters:', filters);
            
            const requestData = {
                filters: filters,
                confirm: true,
                ...confirmationData
            };

            const response = await apiClient.post('/users/api/v1/admin/reset-reward-points/', requestData);
            console.log('Filtered users reward points reset successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error resetting filtered users reward points:', error);
            throw error.response?.data || error.message;
        }
    },

    // Get reward points reset history
    getRewardPointsResetHistory: async (page = 1, pageSize = 10) => {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                page_size: pageSize.toString()
            });

            const response = await apiClient.get(`/users/api/v1/admin/reward-points-reset-history/?${params}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error.message;
        }
    },
// ========== MANUAL POINTS AWARD SERVICES ==========

// Search users for awarding points
searchUsersForAward: async (searchQuery) => {
    try {
        console.log('Searching users for points award:', searchQuery);
        const response = await apiClient.get(`/users/api/v1/admin/award-points/?search=${searchQuery}`);
        console.log('User search results:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error searching users for award:', error);
        throw error.response?.data || error.message;
    }
},

// Award points to a user
awardPointsToUser: async (userIdentifier, pointsAmount, reason) => {
    try {
        console.log('Awarding points to user:', { userIdentifier, pointsAmount, reason });
        const response = await apiClient.post('/users/api/v1/admin/award-points/', {
            user_identifier: userIdentifier,
            points_amount: pointsAmount,
            reason: reason
        });
        console.log('Points awarded successfully:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error awarding points:', error);
        throw error.response?.data || error.message;
    }
},

// Get manual points award history
getPointsAwardHistory: async (page = 1, pageSize = 20, userId = null) => {
    try {
        const params = new URLSearchParams({
            page: page.toString(),
            page_size: pageSize.toString(),
            ...(userId && { user_id: userId })
        });

        console.log('Fetching points award history...');
        const response = await apiClient.get(`/users/api/v1/admin/award-points/history/?${params}`);
        console.log('Award history loaded:', response.data);
        return response.data;
    } catch (error) {
        console.error('Error fetching award history:', error);
        throw error.response?.data || error.message;
    }
},
resetUserPassword: async (userId) => {
        try {
            const token = localStorage.getItem('authToken');
            
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.post(
                `${API_BASE_URL}/users/api/v1/admin/reset-password/${userId}/`,
                {},
                {
                    headers: {
                        'Authorization': `Token ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error('Reset password error:', error);
            throw error;
        }
    }
};

export default UsersService;

export const engagementService = {
    getSettings: async () => {
        const response = await apiClient.get('/users/api/v1/engagement-settings/');
        return response.data;
    },
    updateSettings: async (payload) => {
        const response = await apiClient.put('/users/api/v1/engagement-settings/', payload);
        return response.data;
    },
};
